import { HardHat } from "lucide-react";
import { ABOUT, BRAND, HOME_FAQS, IMG, STATS } from "@/data/site";
import { usePageSeo } from "@/lib/seo";
import { CallButton, QuoteButton } from "@/components/site/Cta";
import BeforeAfter from "@/components/site/BeforeAfter";
import { AreasGrid, CaseStudies, CertStrip, Compliance, Faqs, GalleryStrip, QuoteSection, SectionHead, SectorsGrid, ServicesGrid, Testimonials } from "@/components/site/Sections";

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800" data-testid="hero">
      <img src={IMG.hero} alt="Polished concrete and epoxy flooring in a Gold Coast commercial warehouse" className="absolute inset-0 h-full w-full object-cover opacity-40" fetchPriority="high" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/85 to-[#0F1115]/30" />
      <div className="relative mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 lg:grid-cols-[1.5fr_1fr] lg:py-32">
        <div className="reveal">
          <p className="eyebrow mb-5 flex items-center gap-2"><HardHat className="h-4 w-4" /> All Concept Concrete · Gold Coast</p>
          <h1 className="text-5xl font-extrabold uppercase leading-[0.92] tracking-tight text-white sm:text-6xl lg:text-7xl">
            Polished Concrete, Epoxy & Commercial Flooring <span className="text-brand-500">Gold Coast</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-300 md:text-lg">
            Polished concrete flooring, epoxy flooring and heavy-duty commercial floors for warehouses, showrooms, food production, fitouts and homes — specified to duty, delivered to program.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <CallButton id="hero-phone-cta" className="btn-brand !px-8 !text-xl" />
            <QuoteButton id="hero-quote-cta" />
          </div>
          <p className="mt-4 font-mono text-xs text-slate-500">Speak to an estimator direct · Mon–Fri 6:30am–5pm</p>
        </div>
        <div className="reveal grid grid-cols-2 gap-px self-end bg-slate-700/60 [animation-delay:.2s]">
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#0F1115]/90 p-6 backdrop-blur">
              <div className="font-display text-4xl font-extrabold text-brand-500">{s.value}</div>
              <div className="mt-1 font-mono text-xs uppercase text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const Wrap = ({ children, id, alt }: { children: React.ReactNode; id?: string; alt?: boolean }) => (
  <section id={id} className={`scroll-mt-20 ${alt ? "bg-[#161920]" : ""}`}>
    <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 lg:py-28">{children}</div>
  </section>
);

export default function Home() {
  usePageSeo("/");
  return (
    <>
      <Hero />
      <CertStrip />
      <Wrap id="services">
        <SectionHead eyebrow="Core systems" title="Specialist systems. Specified to duty." sub="From warehouse floors to driveways and pool surrounds — every system is selected for load, chemical exposure, slip rating and return-to-service time." />
        <ServicesGrid />
      </Wrap>
      <Wrap alt>
        <SectionHead eyebrow="Sectors served" title="Built for operators who can't stop." />
        <SectorsGrid />
      </Wrap>
      <Wrap>
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1.5fr]">
          <SectionHead eyebrow="Before / After" title="Preparation is the job." sub="Drag to compare a worn slab with the finished system. Every floor starts with HEPA-extracted diamond grinding and moisture testing — that's what makes the warranty stick." />
          <BeforeAfter />
        </div>
      </Wrap>
      <Wrap alt>
        <SectionHead eyebrow="Case studies" title="Proof on the Gold Coast." />
        <CaseStudies />
      </Wrap>
      <Wrap id="gallery">
        <SectionHead eyebrow="Gallery" title="Recent work." />
        <GalleryStrip />
      </Wrap>
      <Wrap>
        <SectionHead eyebrow="What builders say" title="Trusted by builders & specifiers." />
        <Testimonials />
      </Wrap>
      <Wrap alt>
        <SectionHead eyebrow="Technical & compliance" title="Documented. Tested. Warranted." />
        <Compliance />
      </Wrap>
      <Wrap id="areas">
        <SectionHead eyebrow="Service areas" title="Where we work." sub="Local crews across the Gold Coast, northern corridor, Logan and Northern Rivers NSW." />
        <AreasGrid />
      </Wrap>
      <Wrap alt id="about">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionHead eyebrow="About All Concept Concrete" title="Gold Coast concrete flooring specialists." />
            <p className="text-slate-300" data-testid="about-text">{ABOUT}</p>
            <address className="mt-6 not-italic text-sm text-slate-400" data-testid="about-nap">
              <strong className="text-white">{BRAND.name}</strong><br />
              {BRAND.street}, {BRAND.suburb} {BRAND.region} {BRAND.postcode}<br />
              <a href={BRAND.phoneHref} className="text-brand-400">{BRAND.phone}</a> · <a href={`mailto:${BRAND.email}`} className="text-brand-400">{BRAND.email}</a><br />
              {BRAND.hours}
            </address>
          </div>
          <div>
            <SectionHead eyebrow="FAQ" title="Polished concrete & epoxy questions." />
            <Faqs items={HOME_FAQS.slice(1)} />
          </div>
        </div>
      </Wrap>
      <QuoteSection />
    </>
  );
}
